import React, { useCallback, useEffect } from 'react';

import { ButtonMenuItem, LinkMenuItem, MenuListItem } from '@atlaskit/navigation-system';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/expandable-menu-item';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';

import { ObjectListFlyout } from '../../../ui/object-list-flyout';
import { type ObjectListFlyoutProps } from '../../../ui/object-list-flyout/types';
import { withSideNavigationAnalyticsListener } from '../../analytics/context';
import type { NavItemConfigInternal } from '../../types';
import { omitSafely } from '../../utils/omit-safely';
import {
	isButtonItemConfig,
	isCustomItemConfig,
	isExpandableItemConfig,
	isFlyoutItemConfig,
	isLinkItemConfig,
	isObjectListFlyoutItemConfig,
} from '../../utils/type-guards';
import { useNavAnalytics } from '../../utils/use-nav-analytics';

import { useExpandOpenState } from './use-expand-open-state';
import { useItemDragAndDrop } from './use-item-drag-and-drop';

interface NavItemProps {
	id: string;
	isInMoreMenu?: boolean;
	item: NavItemConfigInternal;
	onClick?: () => void;
	// only used for custom items
	onPeek?: () => void;
	peek?: boolean;
}

export default function NavItem({
	id,
	item,
	peek = false,
	isInMoreMenu = false,
	onClick,
	onPeek,
}: NavItemProps): React.ReactNode {
	const { type } = item;

	const { isExpandOrFlyoutOpen, onExpandFlyoutToggle, isExpandable } = useExpandOpenState(
		item,
		peek,
	);

	const {
		draggableRef,
		isItemDraggable,
		isDragging,
		dropTargetRef,
		dropIndicator,
		maybeDragPreview,
	} = useItemDragAndDrop({
		item,
		id,
		isExpandable,
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isInMoreMenu,
		peek,
	});

	const {
		fireClickedSideNavItemEvent,
		fireClickedSideNavSubMenuItemEvent,
		fireViewedSideNavSubMenuEvent,
	} = useNavAnalytics();

	// Common onSubMenuItemClick handler for all sub-menu item types
	const onSubMenuItemClick = useCallback(() => {
		fireClickedSideNavSubMenuItemEvent(id, undefined, isInMoreMenu);
	}, [fireClickedSideNavSubMenuItemEvent, id, isInMoreMenu]);

	useEffect(() => {
		if (isExpandable && isExpandOrFlyoutOpen) {
			fireViewedSideNavSubMenuEvent(id, undefined, isInMoreMenu);
		}
	}, [fireViewedSideNavSubMenuEvent, id, isExpandOrFlyoutOpen, isExpandable, isInMoreMenu, type]);

	switch (true) {
		case isCustomItemConfig(item): {
			// If the item is a custom item, render it directly
			// TODO: Add customizationHandlers prop when ready
			return (
				<MenuListItem>
					<item.Component
						isInMoreMenu={isInMoreMenu && !peek}
						isPeeked={peek}
						onClick={(...args) => {
							item.Component.defaultProps?.onClick?.(...args);
							fireClickedSideNavItemEvent(id);
							onClick?.();
						}}
						onPeek={onPeek}
						hasDragIndicator={isItemDraggable ? isItemDraggable : undefined}
						draggableRef={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					/>
					{maybeDragPreview}
				</MenuListItem>
			);
		}
		case isLinkItemConfig(item): {
			const { label, labelElem, ...linkMenuItemProps } = omitSafely(item, 'type');
			return (
				<>
					<LinkMenuItem
						{...linkMenuItemProps}
						onClick={(...args) => {
							linkMenuItemProps.onClick?.(...args);
							fireClickedSideNavItemEvent(id);
							onClick?.();
						}}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					>
						{labelElem ?? label}
					</LinkMenuItem>
					{maybeDragPreview}
				</>
			);
		}
		case isButtonItemConfig(item): {
			const { label, labelElem, ...buttonMenuItemProps } = omitSafely(item, 'type');
			return (
				<>
					<ButtonMenuItem
						{...buttonMenuItemProps}
						onClick={(...args) => {
							buttonMenuItemProps.onClick?.(...args);
							fireClickedSideNavItemEvent(id);
							onClick?.();
						}}
						hasDragIndicator={isItemDraggable}
						ref={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					>
						{labelElem ?? label}
					</ButtonMenuItem>
					{maybeDragPreview}
				</>
			);
		}
		case isExpandableItemConfig(item): {
			const { children, elemBefore, label, labelElem, ...triggerProps } = omitSafely(
				item,
				'type',
				'isDefaultExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
				'isExpanded', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
				'onExpansionToggle', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
			);

			// Wrap children with our analytics listener to capture their click events
			const wrappedChildren = withSideNavigationAnalyticsListener(
				item.children,
				onSubMenuItemClick,
			);

			return (
				<>
					<ExpandableMenuItem
						isExpanded={isExpandOrFlyoutOpen}
						onExpansionToggle={onExpandFlyoutToggle}
						dropIndicator={dropIndicator}
						ref={dropTargetRef}
					>
						<ExpandableMenuItemTrigger
							{...triggerProps}
							onClick={(...args) => {
								triggerProps.onClick?.(...args);
								fireClickedSideNavItemEvent(id);
								onClick?.();
							}}
							// During drags, custom elemBefore is not shown
							// https://atlassian.design/components/navigation-system/side-nav-items/drag-and-drop#drag-start
							elemBefore={isDragging ? null : elemBefore}
							hasDragIndicator={isItemDraggable}
							ref={draggableRef}
							isDragging={isDragging}
						>
							{labelElem ?? label}
						</ExpandableMenuItemTrigger>
						<ExpandableMenuItemContent>{wrappedChildren}</ExpandableMenuItemContent>
					</ExpandableMenuItem>
					{maybeDragPreview}
				</>
			);
		}
		case isFlyoutItemConfig(item): {
			const { children, label, labelElem, ...triggerProps } = omitSafely(
				item,
				'type',
				'isDefaultOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
				'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in triggerProps
				'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in triggerProps
			);

			// Wrap children with our analytics listener to capture their click events
			const wrappedChildren = withSideNavigationAnalyticsListener(
				item.children,
				onSubMenuItemClick,
			);

			return (
				<>
					<FlyoutMenuItem isOpen={isExpandOrFlyoutOpen}>
						<FlyoutMenuItemTrigger
							{...triggerProps}
							onClick={(...args) => {
								triggerProps.onClick?.(...args);
								// Toggle the flyout open state
								onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
								fireClickedSideNavItemEvent(id);
								onClick?.();
							}}
							hasDragIndicator={isItemDraggable}
							ref={draggableRef}
							isDragging={isDragging}
							dropIndicator={dropIndicator}
							visualContentRef={dropTargetRef}
						>
							{labelElem ?? label}
						</FlyoutMenuItemTrigger>
						<FlyoutMenuItemContent>{wrappedChildren}</FlyoutMenuItemContent>
					</FlyoutMenuItem>
					{maybeDragPreview}
				</>
			);
		}
		case isObjectListFlyoutItemConfig(item): {
			const objectListFlyoutProps = omitSafely(
				item,
				'type',
				'isOpen', // Handled above with isExpandOrFlyoutOpen - listed here so it doesn't go in objectListFlyoutProps
				'onOpenChange', // Handled above with onExpandFlyoutToggle - listed here so it doesn't go in objectListFlyoutProps
			);

			return (
				<>
					<ObjectListFlyout
						{...objectListFlyoutProps}
						onClick={(...args) => {
							// Toggle the flyout open state
							onExpandFlyoutToggle(!isExpandOrFlyoutOpen);
							(objectListFlyoutProps as ObjectListFlyoutProps).onClick?.(...args);
							fireClickedSideNavItemEvent(id);
							onClick?.();
						}}
						onSubMenuItemClick={onSubMenuItemClick}
						isOpen={isExpandOrFlyoutOpen}
						onOpenChange={onExpandFlyoutToggle}
						hasDragIndicator={isItemDraggable}
						triggerRef={draggableRef}
						isDragging={isDragging}
						dropIndicator={dropIndicator}
						visualContentRef={dropTargetRef}
					/>
					{maybeDragPreview}
				</>
			);
		}
		default:
			return null;
	}
}
